import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";

import { LoansContext } from "../../context/LoansContext";
import { RootContext } from "../../context/RootContext";

import MitigationFactorSelect from "./MitigationFactorSelect";
import {
  WaiverStatus,
  WaiverServiceRecord,
  MIN_MITIGATION_FACTORS,
  asMitigationFactorArray,
  getWaiverMitigationDescriptions,
  getLatestWaiverResponse,
  persistWaiverAction,
  fetchAndSyncWaivers,
  WAIVER_SERVICE_RESPONSE_TYPE,
} from "./utils";

export default function WaiverActionForm({
  waiver,
  onClose,
}: {
  waiver: WaiverServiceRecord;
  onClose?: () => void;
}) {
  const { t } = useTranslation();
  const { setLoanDetails, setWaiverDecisionVersion } = useContext(LoansContext) ?? {};
  const { decodedTokenData } = useContext(RootContext) ?? {};

  const latestResponse = getLatestWaiverResponse(waiver);

  // Independent from the processor's waiverMitigates/description — this is
  // only ever read from the underwriter's own last-recorded compensating
  // factors once one exists; the processor's field is used solely as the
  // one-time pre-fill on the very first "Act on Waiver", and is never
  // written back to.
  const [compensatingFactor, setCompensatingFactor] = useState<string[]>(
    asMitigationFactorArray(
      latestResponse?.compensatingFactors ?? getWaiverMitigationDescriptions(waiver),
    ),
  );
  const [comments, setComments] = useState(latestResponse?.comments ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [saveError, setSaveError] = useState("");

  const isFactorSelectionValid =
    compensatingFactor.length >= MIN_MITIGATION_FACTORS;
  const isActionDisabled = !isFactorSelectionValid || isSaving;

  const handleAction = async (status: string) => {
    setPendingAction(status);
    setIsSaving(true);
    setSaveError("");
    try {
      // Appended as a new entry in the waiver's response history rather than
      // overwriting anything, so a prior outcome's audit trail is never lost
      // across repeated act -> reset -> act cycles.
      await persistWaiverAction(
        setLoanDetails,
        waiver,
        {
          responseType: WAIVER_SERVICE_RESPONSE_TYPE[status],
          // responseBy is resolved to a display name via useUserDisplayName
          // (a Keycloak-userId -> profile lookup, same mechanism as
          // createdBy on the raw waiver-service record). In practice the
          // /online-sizer/users lookup regularly fails to resolve real
          // Keycloak ids ("Failed to fetch Keycloak user ...") — the lookup
          // falls back to echoing whatever value it was given on any miss,
          // so sending a human-readable name here (rather than user_id)
          // means the fallback always displays correctly, lookup success or
          // not — the same reason createdBy already reads fine today.
          responseBy:
            decodedTokenData?.name ||
            decodedTokenData?.email ||
            decodedTokenData?.user_id ||
            "unknown",
          responseDate: new Date().toISOString(),
          isApproved: status !== WaiverStatus.REJECTED,
          compensatingFactors: compensatingFactor,
          comments,
        },
        setWaiverDecisionVersion,
      );
      // The waiver-service auto-posts this decision's comment as a new,
      // decision-tagged chat line (see WaiverChat.tsx's decision-tag chip)
      // as a side effect of the response call above — persistWaiverAction's
      // own local merge only appends to waiverResponses, so without this the
      // chat wouldn't show that line until an unrelated refresh happened to
      // re-fetch the loan's waivers. Re-fetches the whole list (the only
      // fetch primitive available — there's no get-single-waiver endpoint)
      // and fully replaces waiverDetails/generalWaiverDetails, same as every
      // other post-action call site (Header.tsx's escalation flow, etc.).
      await fetchAndSyncWaivers(waiver.sizerLoanId, setLoanDetails, setWaiverDecisionVersion);
      onClose?.();
    } catch (err) {
      setSaveError(t("waiverActionFailed"));
    } finally {
      setIsSaving(false);
      setPendingAction(null);
    }
  };

  return (
    <Box
      data-testid="waiver-action-form"
      sx={{
        // marginTop: "24px",
        borderRadius: "6px",
        // border: "1px solid #B8B8C7",
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "16px 16px 0px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            // border: "1px solid #DBE2FF",
            // background: "#F8F9FD",
            width: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3
              style={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                margin: 0,
                padding: 0,
              }}
            >
              {t("actOnWaiver")}
            </h3>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon fontSize="small" />}
              onClick={onClose}
              disabled={isSaving}
              sx={{ borderRadius: "20px", textTransform: "none" }}
              data-testid="waiver-action-cancel-button"
            >
              {t("cancelReview")}
            </Button>
          </div>
        </Grid>
      </Grid>

      <Grid container style={{ padding: "16px" }}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <label
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
              htmlFor="compensating-factor-label"
            >
              {t("compensatingFactor")} <sup style={{ color: "red" }}>*</sup>
            </label>
            <MitigationFactorSelect
              value={compensatingFactor}
              onChange={setCompensatingFactor}
              labelId="compensating-factor-label"
              testId="compensating-factor-select"
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} style={{ paddingTop: "16px" }}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 300,
            }}
          >
            {t("optionalComments")}
          </span>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={comments}
            onChange={(event) => setComments(event.target.value)}
            data-testid="waiver-action-comments-input"
            style={{ background: "#F8F9FD" }}
          />
        </Grid>

        {saveError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>
              {saveError}
            </div>
          </Grid>
        ) : null}

        {!isFactorSelectionValid ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#B8860B", fontSize: "12px" }}>
              {t("minCompensatingFactorsHint", {
                count: MIN_MITIGATION_FACTORS,
              })}
            </div>
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
            paddingTop: "16px",
          }}
        >
          <LoadingButton
            variant="outlined"
            color="error"
            fullWidth
            startIcon={
              <ClearOutlinedIcon
                fontSize="small"
                sx={{ color: isActionDisabled ? "#B8B8C7" : "#B30C00" }}
              />
            }
            loading={isSaving && pendingAction === WaiverStatus.REJECTED}
            disabled={isActionDisabled}
            onClick={() => handleAction(WaiverStatus.REJECTED)}
            data-testid="waiver-reject-button"
            style={{
              borderRadius: "6px",
              border: `1px solid ${isActionDisabled ? "#B8B8C7" : "#B30C00"}`,
              display: "flex",
              padding: "8px 12px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              flex: "1 0 0",
              color: isActionDisabled ? "#B8B8C7" : "#B30C00",
            }}
          >
            {t("rejectText")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            fullWidth
            startIcon={<DoneOutlinedIcon fontSize="small" sx={{ color: "white" }} />}
            loading={isSaving && pendingAction === WaiverStatus.APPROVED}
            disabled={isActionDisabled}
            onClick={() => handleAction(WaiverStatus.APPROVED)}
            data-testid="waiver-approve-button"
            style={{
              borderRadius: "6px",
              background: isActionDisabled ? "#B8B8C7" : "#2D6C45",
              display: "flex",
              padding: "8px 12px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              flex: "1 0 0",
              color: "white",
            }}
          >
            {t("approveText")}
          </LoadingButton>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
            paddingTop: "16px",
          }}
        >
          <LoadingButton
            variant="outlined"
            fullWidth
            startIcon={
              <ReportProblemOutlinedIcon
                fontSize="small"
                style={{ color: isActionDisabled ? "#B8B8C7" : "#FDA31B" }}
              />
            }
            loading={
              isSaving && pendingAction === WaiverStatus.APPROVED_CONDITIONALLY
            }
            disabled={isActionDisabled}
            onClick={() => handleAction(WaiverStatus.APPROVED_CONDITIONALLY)}
            data-testid="waiver-approve-conditionally-button"
            style={{
              borderRadius: "6px",
              border: `1px solid ${isActionDisabled ? "#B8B8C7" : "#FDA31B"}`,
              display: "flex",
              padding: "8px 12px",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              alignSelf: "stretch",
              color: isActionDisabled ? "#B8B8C7" : "#FDA31B",
            }}
          >
            {t("approveConditionallyWaiver")}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
